<template>
  <el-dialog
    title="修改用户会员等级"
    :visible.sync="dialogVisible"
    width="600px"
    :before-close="handleClose"
    class="membership-dialog"
  >
    <div class="dialog-content">
      <!-- 用户信息 -->
      <div class="user-summary">
        <div class="avatar-container">
          <img v-if="user.avatar" :src="user.avatar" class="user-avatar" />
          <div v-else class="default-avatar">
            <i class="el-icon-user"></i>
          </div>
        </div>
        <div class="user-info">
          <div class="nickname">{{ user.nickname || '暂无昵称' }}</div>
          <div class="phone">{{ user.phone || '暂无手机号' }}</div>
        </div>
      </div>

      <!-- 当前等级 -->
      <div class="current-level">
        <div class="level-label">当前等级</div>
        <div v-if="user.membership_level" class="level-badge" :style="{ background: getMembershipLevelColor(user.membership_level.id, user.membership_level.sortOrder) }">
          <div class="level-name">{{ user.membership_level.levelName || user.membership_level.level_name }}</div>
          <div class="level-discount">{{ formatDiscountRate(user.membership_level.discountRate || user.membership_level.discount_rate) }}</div>
        </div>
        <div v-else class="no-level">暂无等级</div>
      </div>

      <!-- 选择新等级 -->
      <el-form ref="form" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="新等级" prop="membership_level_id">
          <div class="level-options">
            <div 
              v-for="level in membershipLevels" 
              :key="level.id" 
              class="level-option"
              :class="{ 
                active: form.membership_level_id === level.id,
                current: user.membership_level && user.membership_level.id === level.id 
              }"
              @click="selectLevel(level.id)"
            >
              <div class="level-color" :style="{ background: getMembershipLevelColor(level.id, level.sortOrder) }"></div>
              <div class="level-info">
                <div class="level-name">{{ level.levelName || level.level_name }}</div>
                <div class="level-discount">{{ formatDiscountRate(level.discountRate || level.discount_rate) }}</div>
              </div>
              <div v-if="form.membership_level_id === level.id" class="check-icon">
                <i class="el-icon-check"></i>
              </div>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="操作原因" prop="reason">
          <el-input 
            v-model="form.reason" 
            type="textarea" 
            rows="3" 
            placeholder="请输入修改会员等级的原因（选填）"
          />
        </el-form-item>
      </el-form>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="loading">
        确定修改
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getUserDetail, updateUserMembership, formatDiscountRate, getMembershipLevelColor } from '@/api/user'
import { getMembershipLevelList } from '@/api/membershipLevel'

export default {
  name: 'MembershipLevelDialog',
  
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    userId: {
      type: Number,
      default: null
    }
  },
  
  data() {
    return {
      loading: false,
      loadingUser: false,
      user: {},
      membershipLevels: [],
      form: {
        membership_level_id: null,
        reason: ''
      },
      rules: {
        membership_level_id: [
          { required: true, message: '请选择新的会员等级', trigger: 'change' }
        ]
      }
    }
  },
  
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  
  watch: {
    visible(val) {
      if (val && this.userId) {
        this.loadUser()
        this.loadMembershipLevels()
      }
    }
  },
  
  methods: {
    async loadUser() {
      if (!this.userId) return
      
      this.loadingUser = true
      try {
        const res = await getUserDetail(this.userId)
        this.user = res.data
        this.resetForm()
      } catch (error) {
        console.error('获取用户信息失败:', error)
        this.$message.error('获取用户信息失败')
      } finally {
        this.loadingUser = false
      }
    },
    
    resetForm() {
      this.form = {
        membership_level_id: this.user.membership_level ? this.user.membership_level.id : null,
        reason: ''
      }
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },
    
    // 加载会员等级列表（仅启用状态）
    async loadMembershipLevels() {
      try {
        const res = await getMembershipLevelList({ 
          is_active: true,  // 只获取启用状态的会员等级
          page_size: 100    // 获取所有启用的等级
        })
        // 根据API文档，数据结构是 res.data.list
        this.membershipLevels = res.data?.list || []
        console.log('加载会员等级列表:', this.membershipLevels)
      } catch (error) {
        console.error('获取会员等级列表失败:', error)
        this.$message.error('获取会员等级列表失败')
      }
    },
    
    selectLevel(levelId) {
      this.form.membership_level_id = levelId
    },
    
    async handleSubmit() {
      try {
        await this.$refs.form.validate()
        
        // 检查是否与当前等级相同
        if (this.user.membership_level && this.form.membership_level_id === this.user.membership_level.id) {
          this.$message.warning('新等级与当前等级相同')
          return
        }
        
        this.loading = true
        
        await updateUserMembership(this.userId, {
          membership_level_id: this.form.membership_level_id,
          reason: this.form.reason || '管理员手动修改'
        })
        
        this.$message.success('会员等级修改成功')
        this.$emit('success')
        this.handleClose()
        
      } catch (error) {
        if (error !== 'cancel') {
          console.error('修改会员等级失败:', error)
          this.$message.error(error.message || '修改会员等级失败')
        }
      } finally {
        this.loading = false
      }
    },
    
    handleClose() {
      this.dialogVisible = false
    },
    
    formatDiscountRate,
    getMembershipLevelColor
  }
}
</script>

<style scoped>
.membership-dialog :deep(.el-dialog) {
  border-radius: 16px;
}

.membership-dialog :deep(.el-dialog__header) {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 20px;
  border-radius: 16px 16px 0 0;
}

.membership-dialog :deep(.el-dialog__title) {
  color: white;
  font-weight: 600;
}

.membership-dialog :deep(.el-dialog__headerbtn .el-dialog__close) {
  color: white;
}

.dialog-content {
  padding: 10px 0;
}

.user-summary {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  margin-bottom: 20px;
}

.avatar-container {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.user-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.default-avatar {
  width: 100%;
  height: 100%;
  background: #e0e3e8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #909399;
}

.user-info {
  flex: 1;
}

.nickname {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
}

.phone {
  font-size: 14px;
  color: #7f8c8d;
}

.current-level {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: #fff;
  border: 2px solid #e4e7ed;
  border-radius: 12px;
  margin-bottom: 30px;
}

.level-label {
  font-size: 16px;
  font-weight: 600;
  color: #606266;
  min-width: 80px;
}

.level-badge {
  padding: 15px 30px;
  border-radius: 8px;
  color: white;
  text-align: center;
}

.level-name {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 5px;
}

.level-discount {
  font-size: 14px;
  opacity: 0.9;
}

.no-level {
  color: #909399;
  font-size: 14px;
}

.level-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.level-option {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  border: 2px solid #e4e7ed;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.level-option:hover {
  border-color: #409eff;
  box-shadow: 0 2px 12px rgba(64, 158, 255, 0.2);
  transform: translateY(-2px);
}

.level-option.active {
  border-color: #409eff;
  background: #ecf5ff;
}

.level-option.current {
  opacity: 0.6;
  cursor: not-allowed;
}

.level-option.current:hover {
  transform: none;
  box-shadow: none;
}

.level-color {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  flex-shrink: 0;
}

.level-info {
  flex: 1;
}

.level-info .level-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
}

.level-info .level-discount {
  font-size: 14px;
  color: #7f8c8d;
}

.check-icon {
  width: 30px;
  height: 30px;
  background: #409eff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
}
</style>
